import { PrimitiveAtom, useAtom } from "jotai";
import apiAgent from "../../api/apiAgent";
import { IPublishState, changesSchema, examplePublishAtom } from "./atoms";
import { tMsg } from "../ToastNotifications";
import { publishGroups } from "./publishConfig";
import _ from "lodash";
import { useOriginParams } from "../../utils";

const getGroupDataFromModel = (
  modelName: string
): {
  group: string | undefined;
  atom: PrimitiveAtom<IPublishState>; // this needs a default atom
} => {
  const groupKeys = Object.keys(publishGroups);
  const group = groupKeys.find(
    (key) =>
      _.includes(
        publishGroups[key as keyof typeof publishGroups].models,
        modelName
      ) ||
      _.includes(
        publishGroups[key as keyof typeof publishGroups].singleItems,
        modelName
      )
  );

  if (!group) return { group, atom: examplePublishAtom }; // <-- this needs a default atom
  const atom = publishGroups[group as keyof typeof publishGroups].atom;
  return {
    group,
    atom,
  };
};

const getPublishStates = async (
  name: keyof typeof publishGroups,
  setAtomFunction: (state: IPublishState) => void
) => {
  try {
    const response = await apiAgent.SingleItem.get(`changes-${name}`);
    if (response.status === 200) {
      const data = JSON.parse(response.data.data);
      const objectStore = {
        lastpublish: data.lastpublish,
        lastchange: data.lastchange,
      };
      setAtomFunction(objectStore);
    }
  } catch (err) {
    console.log(err);
    tMsg.error(`Unable to retrieve ${name} publishing data`);
    setAtomFunction({
      lastchange: null,
      lastpublish: null,
    });
  }
};

const updatePublishTimes = async (
  groupName: keyof typeof publishGroups,
  setAtom: any // SetAtom
) => {
  try {
    const now = new Date().toLocaleString();
    const updateState = { lastchange: now, lastpublish: now };
    const saveDateResponse = await apiAgent.SingleItem.update(
      `changes-${groupName}`,
      updateState,
      changesSchema
    );

    console.log(saveDateResponse);
    if (saveDateResponse.status === 200) {
      setAtom(updateState);
    }
  } catch (err) {
    tMsg.warning("Error saving publish dates");
    console.log(err);
    return undefined;
  }
};

const resetPublishingChanges = async (name: keyof typeof publishGroups) => {
  const response = await apiAgent.SingleItem.update(
    `changes-${name}`,
    { lastchange: "", lastpublish: "" },
    changesSchema
  );
  // console.log(response);
  if (response.status === 200) {
    tMsg.info(`${name}changes have been reset`);
  } else {
    console.log(response.statusText);
    tMsg.warning("Error resetting");
  }
};

const useSetChangesMade = (modelName: string) => {
  // determine which app group
  const [originFrom] = useOriginParams();

  const { group, atom } = getGroupDataFromModel(
    originFrom ? originFrom : modelName
  );
  const [publishState, setPublishState] = useAtom(atom);

  if (!group) return () => console.log(`${modelName} has no singleItemsConfig`); //tMsg.warning("Could not update 'last modified date.'");

  return async () => {
    const newData = {
      lastchange: new Date().toLocaleString(),
      lastpublish: publishState.lastpublish,
    };
    const response = await apiAgent.SingleItem.update(
      `changes-${group}`,
      newData,
      changesSchema
    );
    if (response.status === 200) {
      setPublishState(newData);
    } else {
      console.log(response.statusText);
      tMsg.warning("Error updating change dates");
    }
  };
};

const checkAndAddPublishData = async (
  publishKey: string,
  data: any,
  schema: any
) => {
  const response = await apiAgent.SingleItem.get(`changes-${publishKey}`);
  if (response) {
    console.log(`Publish data for ${publishKey} already exists`, response.data);
    return true;
  }
  try {
    const createResponse = await apiAgent.SingleItem.create(
      `changes-${publishKey}`,
      data,
      schema
    );
    if (createResponse.status === 200)
      tMsg.success(`Publish data added: ${publishKey}`);
  } catch {
    tMsg.warning(`Publish data could not be added: ${publishKey}`);
  }
};

const addAnyNewPublishData = async () => {
  const now = new Date().toLocaleString();
  const schema = changesSchema;
  const data = { lastchange: now, lastpublish: now };
  const publishKeys = Object.keys(publishGroups);

  const allCalls: Promise<any>[] = publishKeys.map((publishKey) =>
    checkAndAddPublishData(publishKey, data, schema)
  );

  try {
    await Promise.all(allCalls);
  } catch {
    tMsg.warning("Error adding publish data");
  }
};

export {
  getGroupDataFromModel,
  getPublishStates,
  resetPublishingChanges,
  useSetChangesMade,
  updatePublishTimes,
  addAnyNewPublishData,
};
